import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { cn } from '@/shared/lib/utils'
import {
  getMachineryDirectiveOnboardingStepHeader,
  MachineryDirectiveOnboardingStepsOverview,
} from './MachineryDirectiveOnboardingStepsOverview'
import type { MachineryDirectiveOnboardingSidebarStepId } from './machineryDirectiveOnboardingSteps'
import type { OnboardingStep } from './machineryDirectiveOnboardingTypes'

export type MachineryDirectiveOnboardingLayoutProps = {
  currentStep: OnboardingStep
  furthestProgressSidebarStepId: MachineryDirectiveOnboardingSidebarStepId
  productName: string | null
  backHref?: string
  showOnboardingBack?: boolean
  isBackDisabled?: boolean
  onBack?: () => void
  onNavigateToSidebarStep?: (
    stepId: MachineryDirectiveOnboardingSidebarStepId
  ) => void
  children: React.ReactNode
}

export function MachineryDirectiveOnboardingLayout({
  currentStep,
  furthestProgressSidebarStepId,
  productName,
  backHref,
  showOnboardingBack = false,
  isBackDisabled = false,
  onBack,
  onNavigateToSidebarStep,
  children,
}: MachineryDirectiveOnboardingLayoutProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const stepTitle = getMachineryDirectiveOnboardingStepHeader(currentStep, t)
  return (
    <div className='grid min-h-0 flex-1 grid-cols-1 rounded-lg border lg:grid-cols-[minmax(280px,400px)_minmax(0,1fr)]'>
      <aside
        className={cn(
          'flex min-h-0 flex-col border-b border-border bg-muted/40 px-5 py-8 sm:px-8',
          'lg:border-e lg:border-b-0 lg:py-10 lg:ps-8 lg:pe-6'
        )}
      >
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
          <Link
            className='inline-flex w-full min-w-0 items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase hover:text-foreground'
            to={backHref ?? '/'}
            aria-label={
              productName
                ? `${t('nav.backToProduct')}: ${productName}`
                : t('nav.backToProduct')
            }
          >
            <ArrowLeft className='size-4 shrink-0 rtl:rotate-180' aria-hidden />
            <span className='min-w-0 flex-1 truncate text-start'>{productName}</span>
          </Link>
          <p className='mt-2 text-lg font-semibold tracking-tight text-foreground'>
            {t('machineryDirective.title')}
          </p>
          <h1 className='mt-6 text-lg font-semibold tracking-tight'>
            {t('onboarding.overviewTitle')}
          </h1>
          <div className='mt-8'>
            <MachineryDirectiveOnboardingStepsOverview
              currentStep={currentStep}
              furthestProgressSidebarStepId={furthestProgressSidebarStepId}
              isStepNavigationDisabled={isBackDisabled}
              onNavigateToSidebarStep={onNavigateToSidebarStep}
            />
          </div>
        </div>
      </aside>

      <main className='flex min-h-0 flex-col overflow-y-auto'>
        <div
          className={cn(
            'mx-auto flex w-full flex-1 flex-col px-4 py-8 sm:px-8 lg:py-10'
          )}
        >
          <header className='mb-8 space-y-4'>
            {showOnboardingBack && onBack != null && (
              <div>
                <button
                  type='button'
                  className={cn(
                    'inline-flex min-w-0 items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase hover:text-foreground',
                    'disabled:pointer-events-none disabled:opacity-60'
                  )}
                  disabled={isBackDisabled}
                  onClick={onBack}
                >
                  <ArrowLeft
                    className='size-4 shrink-0 rtl:rotate-180'
                    aria-hidden
                  />
                  <span>{t('onboarding.backToPreviousStep')}</span>
                </button>
              </div>
            )}
            <h2 className='text-xl font-semibold tracking-tight sm:text-2xl'>
              {stepTitle}
            </h2>
          </header>
          {children}
        </div>
      </main>
    </div>
  )
}
